export type KeepDecision = 'Keep' | 'Keep with changes' | 'Retire' | 'Needs discussion' | ''

export type Product = {
  id: string
  /** What is in Salesforce today — read-only, and the rules enforce that. */
  family: string
  name: string
  sku: string
  activeToday: boolean
  soldLast12: number
  soldAllTime: number
  /**
   * Live subscriptions on this product in the legacy org, and the Product2 it was counted
   * against — see `activeSubscriptions.ts`. Absent until Milestone has measured it.
   */
  activeSubscriptions?: number
  product2Id?: string | null
  activeSubscriptionsAsOf?: string
  lastSold: string
  typicalPrice: number | null
  /** The client's answers. */
  keep: KeepDecision
  familyFinal: string
  nameFinal: string
  skuFinal: string
  vendastaSku: string
  listPrice: string
  /**
   * A one-off charge on top of the list price. Recorded as typed rather than as a number,
   * the same as `listPrice` — people write "500", "$500" and "500 per site", and losing
   * what they meant to a parse is worse than carrying the string.
   */
  setupFee?: string
  billing: string
  /**
   * What the product is, in the client's own words. Three questions rather than one
   * description box because they are answered by different people and used for different
   * things: what the work actually is, why somebody buys it, and what they get out of it.
   */
  activity?: string
  purpose?: string
  benefits?: string
  filledInBy: string
  notes: string
  decided: boolean
  /**
   * Sold as several products together. A bundle is an ordinary product row carrying the
   * list of what it holds — see `bundles.ts` for why it is not a second kind of record.
   */
  isBundle?: boolean
  /** Product ids this bundle contains, in the order they were chosen. */
  bundleItems?: string[]
  recentlySold: boolean
  looksNonProduct: boolean
  duplicateSku: boolean
  decidedBy?: string | null
  decidedAt?: { seconds: number } | null
  /**
   * True for a product the client added here — the Salesforce export never had it, so it
   * has no measured evidence and none of the grey columns describe the legacy org.
   */
  added?: boolean
  addedBy?: string | null
  addedAt?: { seconds: number } | null
}

export const KEEP_OPTIONS: Exclude<KeepDecision, ''>[] = [
  'Keep', 'Keep with changes', 'Retire', 'Needs discussion',
]

export const BILLING_OPTIONS = ['One time', 'Subscription', 'Both', 'Not billed']

export const keepTone = (k: string) =>
  k === 'Keep' ? 'done'
  : k === 'Keep with changes' ? 'primary'
  : k === 'Retire' ? 'neutral'
  : k === 'Needs discussion' ? 'critical'
  : 'high'

/**
 * The catalogue is far larger than the business it serves: 246 rows are active in
 * Salesforce but only 68 sold anything in the last year. This marks the rows that carry
 * no evidence of being in use, which is where retirement starts.
 */
export const isDormant = (p: Product) => p.activeToday && p.soldLast12 === 0

/**
 * A SKU that is blank or shared cannot be an external id, so it cannot key the migration
 * or match a Vendasta or Stripe record. This is the problem to settle before mapping.
 */
export const hasSkuProblem = (p: Product) => p.sku === '' || p.duplicateSku

export const money = (v: number | null) =>
  v === null ? '—' : `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`

/**
 * The families the catalogue actually uses, for the final-answer dropdown.
 *
 * Built from the data rather than kept as a fixed list, because consolidating the families
 * is part of what this review decides: a family somebody has already chosen as a final
 * answer has to be offered to the next person, or everyone invents their own spelling of
 * it and the consolidation produces more families than it started with.
 */
export const familyOptions = (products: Product[]): string[] => {
  const seen = new Set<string>()
  for (const p of products) {
    for (const value of [p.familyFinal, p.family]) {
      const trimmed = (value ?? '').trim()
      if (trimmed) seen.add(trimmed)
    }
  }
  return [...seen].sort((a, b) => a.localeCompare(b))
}

/**
 * Who may take a product off the list.
 *
 * An imported row is evidence of what Salesforce holds, so only Milestone removes one —
 * a client deleting it would destroy the very thing their answers are meant to rest on.
 * A row added here belongs to whoever may answer the list, the same way an added picklist
 * value does. `firestore.rules` enforces exactly this; the function is what decides
 * whether the button is there to press, so the two have to say the same thing.
 */
export const canDeleteProduct = (
  product: Product, actor: { isAdmin: boolean; canDelete: boolean },
) => actor.isAdmin || (actor.canDelete === true && product.added === true)

/**
 * What a product will be called, and filed under, in the new org.
 *
 * The final answers win everywhere the catalogue is *read*, because the review exists to
 * decide what the new org holds and a list showing only the old names cannot be checked
 * against that. What they never do is overwrite the Salesforce values on the record: those
 * stay exactly as imported, are shown beside the final wherever they differ, and remain
 * the evidence the answers were given against.
 */
const answered = (value: string | undefined, fallback: string) =>
  (value ?? '').trim() || fallback

export const finalName = (p: Product) => answered(p.nameFinal, p.name)
export const finalFamily = (p: Product) => answered(p.familyFinal, p.family)
export const finalSku = (p: Product) => answered(p.skuFinal, p.sku)

/** True only when the answer actually changes something, so no-op answers stay quiet. */
export const isRenamed = (p: Product) => finalName(p) !== p.name
export const isRefamilied = (p: Product) => finalFamily(p) !== p.family
export const isResku = (p: Product) => finalSku(p) !== p.sku

/**
 * Everything a table search should match a product on.
 *
 * A table's global filter only ever sees the values its columns expose. The SKU is printed
 * under the product name rather than given a column of its own, so without this it is
 * invisible to the search box — and the SKU is how half this catalogue gets identified,
 * which made "Search by name, SKU or family" a promise the box did not keep.
 *
 * Both the answered name and the Salesforce one are in here: the new name is what the list
 * shows, and the old one is what is written on the invoice the person is holding. The
 * final name comes first so that sorting the column sorts by the name the new org will use.
 */
export const productSearchText = (p: Product): string =>
  joinUnique([finalName(p), p.name, finalSku(p), p.sku, p.vendastaSku])

/** Trimmed, de-duplicated, joined — one string for a search to run over. */
const joinUnique = (values: (string | undefined)[]): string =>
  values
    .map((v) => (v ?? '').trim())
    .filter((v, i, all) => v !== '' && all.indexOf(v) === i)
    .join(' ')

/*
 * The SKU and Family cells each print the value Salesforce still holds underneath the
 * answered one — "was SKU-OLD-1" — because the answers were given against it. These are
 * what those two columns' own filters search, so the funnel reaches everything the column
 * draws rather than only its top line. They are not the accessors: `family` is a grouping
 * option, and grouping by a string carrying both families would invent a group per pair.
 */
export const skuSearchText = (p: Product): string => joinUnique([finalSku(p), p.sku])
export const familySearchText = (p: Product): string => joinUnique([finalFamily(p), p.family])
